import { LABELS, type LabelKey } from './labels';
import { getBillingTypeDisplay } from '../utils/billingDisplay';
import { getStatusLabel } from '../types/status';
import { getProgramTypeDisplayName, getProgramTypeGlyph } from '../types/program';
import { formatWeekdayDateDisplay } from '../utils/date';
import { useBilingualEnabled } from './useBilingualEnabled';

/** Maps a program billing_type to its bilingual label key, if one exists. */
const BILLING_TYPE_LABEL_KEY: Record<string, LabelKey> = {
  recurring_monthly: 'billing.monthly',
  fixed_term: 'billing.fixedTerm',
  per_day: 'billing.perDay',
  flex_monthly: 'billing.flexMonthly',
};

const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const EN_MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ZH_MONTHS = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'];
const ZH_WEEKDAYS_LONG = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
const ZH_WEEKDAYS_SHORT = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];

/** Maps a flex plan status to its bilingual label key. */
const FLEX_STATUS_LABEL_KEY: Record<string, LabelKey> = {
  draft: 'flexStatus.draft',
  locked: 'flexStatus.locked',
  billed: 'flexStatus.billed',
  skipped: 'flexStatus.skipped',
};

/** Maps a payment/invoice status to its bilingual label key, if one exists. */
const STATUS_LABEL_KEY: Record<string, LabelKey> = {
  paid: 'status.paid',
  partial: 'status.partial',
  pending: 'status.pending',
  overdue: 'status.overdue',
  voided: 'status.voided',
  payment_processing: 'status.processing',
};

/**
 * Bilingual label accessor (string form). On an English-only brand, returns the
 * English string verbatim. On a bilingual brand (see useBilingualEnabled), returns
 * "中文 English" for inline use on buttons and text.
 *
 *   const t = useLabels();
 *   <button>{t('action.enroll')}</button>   // English-only → "Enroll a Student"
 *                                            // bilingual    → "报名 Enroll a Student"
 *
 * For tight layouts that need to style the two languages separately (e.g. the
 * bottom-nav label stacked under an icon), use useLabelParts() / <BilingualLabel>.
 */
export function useLabels(): (key: LabelKey) => string {
  const bilingual = useBilingualEnabled();
  return (key: LabelKey): string => {
    const { en, zh } = LABELS[key];
    return bilingual ? `${zh} ${en}` : en;
  };
}

/**
 * Structured form for components that render the two languages with different
 * styling. `zh` is null on an English-only brand.
 */
export function useLabelParts(): (key: LabelKey) => { en: string; zh: string | null } {
  const bilingual = useBilingualEnabled();
  return (key: LabelKey) => {
    const { en, zh } = LABELS[key];
    return { en, zh: bilingual ? zh : null };
  };
}

/**
 * Bilingual label for a program billing_type. Returns the same string as
 * getBillingTypeDisplay().label when English-only; "中文 English" when bilingual.
 * Unknown billing types fall back to getBillingTypeDisplay()'s English label.
 * (Pair with getBillingTypeDisplay(type).Icon for the badge icon.)
 */
export function useBillingTypeLabel(): (billingType: string) => string {
  const t = useLabels();
  return (billingType: string): string => {
    const key = BILLING_TYPE_LABEL_KEY[billingType];
    return key ? t(key) : getBillingTypeDisplay(billingType).label;
  };
}

/**
 * Bilingual label for a payment/invoice status. Same string as getStatusLabel()
 * when English-only; "中文 English" when bilingual. Unknown statuses fall back to
 * getStatusLabel().
 */
export function useStatusLabel(): (status: string) => string {
  const t = useLabels();
  return (status: string): string => {
    const key = STATUS_LABEL_KEY[(status || '').toLowerCase()];
    return key ? t(key) : getStatusLabel(status);
  };
}

/**
 * Bilingual label for a program type. Same string as getProgramTypeDisplayName()
 * when English-only; "<glyph> English" when bilingual, where the glyph is the same
 * single character used by the program-type badge (getProgramTypeGlyph) — so the
 * badge and the label always agree (e.g. badge 营 + "营 Camp").
 */
export function useProgramTypeName(): (type: string) => string {
  const bilingual = useBilingualEnabled();
  return (type: string): string => {
    const en = getProgramTypeDisplayName(type);
    return bilingual ? `${getProgramTypeGlyph(type)} ${en}` : en;
  };
}

/**
 * Bilingual month label. Off → "August 2026" (or "Aug 2026" when short); on →
 * "八月 August 2026" / "八月 Aug 2026". Pass `year: null` to omit the year (e.g. tabs).
 *
 *   const month = useMonthLabel();
 *   month(2026, 7)                    // "八月 August 2026"
 *   month(null, 7, { short: true })   // "八月 Aug"
 */
export function useMonthLabel(): (year: number | null, monthIdx: number, opts?: { short?: boolean }) => string {
  const bilingual = useBilingualEnabled();
  return (year, monthIdx, opts) => {
    const en = (opts?.short ? EN_MONTHS_SHORT : EN_MONTHS)[monthIdx];
    const base = bilingual ? `${ZH_MONTHS[monthIdx]} ${en}` : en;
    return year === null ? base : `${base} ${year}`;
  };
}

/**
 * Bilingual label for a flex plan status (draft / locked / billed / skipped).
 * Off → the English word; on → "中文 English". Unknown → the raw status.
 */
export function useFlexStatusLabel(): (status: string) => string {
  const t = useLabels();
  return (status: string): string => {
    const key = FLEX_STATUS_LABEL_KEY[(status || '').toLowerCase()];
    return key ? t(key) : status;
  };
}

/**
 * Bilingual weekday + date, for a `DATE` column. Off → "Monday, Aug 24"; on →
 * "星期一 Monday, Aug 24" (or "周一 Mon, Aug 24" in short form).
 *
 * The weekday is read off the date parts as a local `Date`, exactly as
 * `formatWeekdayDateDisplay` does, so the two can never disagree about which day it is.
 */
export function useWeekdayDateLabel(): (dateString: string, opts?: { weekday?: 'short' | 'long' }) => string {
  const bilingual = useBilingualEnabled();
  return (dateString, opts) => {
    const en = formatWeekdayDateDisplay(dateString, opts);
    if (!bilingual || !en) return en;
    const [datePart] = dateString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const dow = new Date(year, month - 1, day).getDay();
    const zh = (opts?.weekday === 'long' ? ZH_WEEKDAYS_LONG : ZH_WEEKDAYS_SHORT)[dow];
    return `${zh} ${en}`;
  };
}
